'use client'

import CreationTool from 'creation/src/creation-tools/types/creation-tool'
import CreationToolUserRestriction from 'creation/src/creation-tools/types/user-restriction'
import CreationToolWelcomeButton from 'creation/src/creation-tools/types/welcome-button'
import { useCreationStore } from '../store/provider'
import WelcomeButtonCard from './button-card'

type Props = {
    tool: CreationTool
    isStaff: boolean
}

/**
 * Legacy structural reference: components/welcome/buttons/index.tsx.
 *
 * Filters `CreationTool.welcomeButtons(tool)` down to the buttons this user
 * has access to, then splits: the first (up to) three go in one vertical
 * row, anything beyond that spills one-per-row into horizontal cards. Today
 * that only ever happens for Upload with every permission granted, where the
 * fourth button (Super Cut) spills — see
 * docs/plans/2026-08-07-001-creation-welcome-measurements.md §1 for the full
 * permission matrix this was verified against.
 *
 * `userRestrictionAccess` is read from the store rather than taken as a
 * prop: `getCreationAccess(user)` was previously computed twice per request
 * — once server-side into the store (`CreationShell`/layout.tsx) and again
 * as a prop here (page.tsx) — a duplicate source of truth for the same
 * value. Reading the store instead gives its sync effect
 * (store/provider.tsx) a real consumer: a mid-flow permission change (e.g.
 * verifying an account) now reaches this component the same way it already
 * reaches the error modal (error-modal.tsx).
 */
const WelcomeButtons = ({ tool, isStaff }: Props) => {
    const userRestrictionAccess = useCreationStore((state) => state.userRestrictionAccess)
    const buttons = CreationTool.welcomeButtons(tool)

    const filteredButtons = buttons.filter((button) => {
        const restriction = CreationToolWelcomeButton.userRestriction(button)
        return !restriction || CreationToolUserRestriction.hasAccess(restriction, userRestrictionAccess)
    })

    const firstRowButtons = filteredButtons.length > 3 ? filteredButtons.slice(0, 3) : filteredButtons
    const rowButtons = filteredButtons.length > 3 ? filteredButtons.slice(3) : []

    return (
        <div className="w-full">
            <div data-welcome-row className="mb-[15px] flex w-full">
                {firstRowButtons.map((button) => (
                    <WelcomeButtonCard key={button} button={button} isStaff={isStaff} />
                ))}
            </div>
            {rowButtons.map((button) => (
                <div key={button} data-welcome-row="horizontal" className="mb-[15px] flex w-full">
                    <WelcomeButtonCard button={button} isStaff={isStaff} horizontal />
                </div>
            ))}
        </div>
    )
}

export default WelcomeButtons
